
// VisitingCardEditor.js
import React, { useState, useRef, useEffect } from "react";
import html2canvas from "html2canvas";

const baseW = 350;
const baseH = 200;

const defaultData = {
    namePos: { x: 15.8, y: 21.3 },
    ownerPos: { x: 14.9, y: 43.6 },
    phonePos: { x: 37.6, y: 70.2 },
    emailPos: { x: 39.8, y: 98.0 },
    addressPos: { x: 40.8, y: 131.0 },

    businessName: "Tech Hive",
    ownerName: "Aarav Mehta",
    phoneNumber: "+91 9123456780",
    email: "contact@techhive.com",
    address: "Plot 42, Cyber Park,\nBangalore",

    bgUrls: [
        "https://firebasestorage.googleapis.com/v0/b/youvati.appspot.com/o/Vimages%2FPremium%20Card%20Left%20Aligned%20Test%20Blank-01-01.jpg?alt=media&token=74fbc629-d47c-4c50-9862-ce7e0de3933c"
    ],
    bgIndex: 0,

    fontFamily: "Arial",
    fontScale: 1.0,
    textColor: "#ffffff"
};

export default function VisitingCardEditor() {
    const [cardData, setCardData] = useState(defaultData);
    const [bgLoaded, setBgLoaded] = useState(false);
    const containerRef = useRef(null);

    // Load the background image so capturing is more reliable
    useEffect(() => {
        const img = new Image();
        img.crossOrigin = "anonymous"; // 👈 add this
        img.src = cardData.bgUrls[cardData.bgIndex];
        img.onload = () => {
            setBgLoaded(true);
        };
    }, [cardData.bgUrls, cardData.bgIndex]);

    const dragState = useRef({
        draggingKey: null,
        originMouseX: 0,
        originMouseY: 0,
        originPos: { x: 0, y: 0 }
    });

    const handleMouseDown = (key, e) => {
        e.preventDefault();
        dragState.current = {
            draggingKey: key,
            originMouseX: e.clientX,
            originMouseY: e.clientY,
            originPos: { ...cardData[key] }
        };
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = (e) => {
        const ds = dragState.current;
        if (!ds.draggingKey) return;

        const dx = e.clientX - ds.originMouseX;
        const dy = e.clientY - ds.originMouseY;
        const newPos = {
            x: ds.originPos.x + dx,
            y: ds.originPos.y + dy
        };

        setCardData((prev) => ({
            ...prev,
            [ds.draggingKey]: newPos
        }));
    };

    const handleMouseUp = (e) => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        dragState.current.draggingKey = null;
    };

    const captureAndDownload = async () => {
        if (!containerRef.current) {
            console.error("No container ref");
            return;
        }
        // Wait until background is loaded
        if (!bgLoaded) {
            console.warn("Background not yet loaded");
            return;
        }
        try {
            const canvas = await html2canvas(containerRef.current, {
                backgroundColor: null,
                scale: 2, // increase for better resolution
                useCORS: true // 👈 critical for loading cross-origin images
            });
            const dataUrl = canvas.toDataURL("image/png");
            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = `visiting_card_${Date.now()}.png`;
            link.click();
        } catch (err) {
            console.error("Failed to capture:", err);
        }
    };

    // compute font size based on scale and a base
    const computeFontSize = (base) => {
        return base * cardData.fontScale;
    };

    return (
        <div style={{ padding: 20 }}>
            {/* The container that holds the card */}
            <div
                ref={containerRef}
                style={{
                    width: baseW,
                    height: baseH,
                    position: "relative",
                    border: "1px solid #999",
                    overflow: "hidden"
                }}
            >
                {/* Render actual background image as IMG so html2canvas picks it up */}
                <img
                    src={cardData.bgUrls[cardData.bgIndex]}
                    alt="background"
                    crossOrigin="anonymous"
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 0
                    }}
                    onLoad={() => {
                        // Mark that background has loaded
                        setBgLoaded(true);
                    }}
                />

                {/* Text Overlays */}
                {[
                    { key: "namePos", text: cardData.businessName, base: 24, bold: true },
                    { key: "ownerPos", text: cardData.ownerName, base: 18, bold: false },
                    { key: "phonePos", text: cardData.phoneNumber, base: 16, bold: false },
                    { key: "emailPos", text: cardData.email, base: 16, bold: false },
                    { key: "addressPos", text: cardData.address, base: 14, bold: false }
                ].map(({ key, text, base, bold }) => {
                    const pos = cardData[key];
                    return (
                        <div
                            key={key}
                            onMouseDown={(e) => handleMouseDown(key, e)}
                            style={{
                                position: "absolute",
                                left: pos.x,
                                top: pos.y,
                                cursor: "move",
                                whiteSpace: "pre-line",
                                fontFamily: cardData.fontFamily,
                                fontSize: `${computeFontSize(base)}px`,
                                fontWeight: bold ? "bold" : "normal",
                                color: cardData.textColor,
                                textShadow: "0px 0px 2px rgba(0,0,0,0.6)",
                                zIndex: 1
                            }}
                        >
                            {text}
                        </div>
                    );
                })}
            </div>


            <div style={{ marginTop: 20 }}>
                <button onClick={captureAndDownload}>Download Card as Image</button>
            </div>

            <div style={{ marginTop: 30, maxWidth: 400 }}>
                {/* Form for editing text, font scale, color etc. */}
                <div style={{ marginBottom: 12 }}>
                    <label>Business Name: </label>
                    <input
                        type="text"
                        value={cardData.businessName}
                        onChange={(e) =>
                            setCardData((prev) => ({
                                ...prev,
                                businessName: e.target.value
                            }))
                        }
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label>Owner Name: </label>
                    <input
                        type="text"
                        value={cardData.ownerName}
                        onChange={(e) =>
                            setCardData((prev) => ({
                                ...prev,
                                ownerName: e.target.value
                            }))
                        }
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label>Phone: </label>
                    <input
                        type="text"
                        value={cardData.phoneNumber}
                        onChange={(e) =>
                            setCardData((prev) => ({
                                ...prev,
                                phoneNumber: e.target.value
                            }))
                        }
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label>Email: </label>
                    <input
                        type="text"
                        value={cardData.email}
                        onChange={(e) =>
                            setCardData((prev) => ({
                                ...prev,
                                email: e.target.value
                            }))
                        }
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label>Address: </label>
                    <textarea
                        rows={2}
                        value={cardData.address}
                        onChange={(e) =>
                            setCardData((prev) => ({
                                ...prev,
                                address: e.target.value
                            }))
                        }
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label>Font Scale: </label>
                    <input
                        type="number"
                        step="0.1"
                        min="0.5"
                        max="3"
                        value={cardData.fontScale}
                        onChange={(e) =>
                            setCardData((prev) => ({
                                ...prev,
                                fontScale: parseFloat(e.target.value)
                            }))
                        }
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label>Text Color: </label>
                    <input
                        type="color"
                        value={cardData.textColor}
                        onChange={(e) =>
                            setCardData((prev) => ({
                                ...prev,
                                textColor: e.target.value
                            }))
                        }
                    />
                </div>
            </div>
        </div>
    );
}



// // App.js
// import React, { useState, useRef, useEffect } from "react";
// import html2canvas from "html2canvas";
// // You could also import Google Fonts via link in index.html or via @fontsource

// // Data model equivalent
// const defaultCardData = {
//     namePos: { x: 15.8, y: 21.3 },
//     ownerPos: { x: 14.9, y: 43.6 },
//     phonePos: { x: 37.6, y: 70.2 },
//     emailPos: { x: 39.8, y: 98.0 },
//     addressPos: { x: 40.8, y: 131.0 },

//     businessName: "Tech Hive",
//     ownerName: "Aarav Mehta",
//     phoneNumber: "+91 9123456780",
//     email: "contact@techhive.com",
//     address: "Plot 42, Cyber Park,\nBangalore",

//     bgUrls: [
//         "https://firebasestorage.googleapis.com/v0/b/youvati.appspot.com/o/Vimages%2FPremium%20Card%20Left%20Aligned%20Test%20Blank-01-01.jpg?alt=media&token=74fbc629-d47c-4c50-9862-ce7e0de3933c"
//     ],
//     bgIndex: 0,

//     fontFamily: "Roboto",
//     fontScale: 0.7,
//     textColor: "#ffffff"
// };

// function VisitingCardEditor() {
//     const [cardData, setCardData] = useState(defaultCardData);
//     const [bgReady, setBgReady] = useState(false);
//     const cardRef = useRef(null);

//     const baseW = 350;
//     const baseH = 200;
//     const aspect = baseW / baseH;

//     // Load background image
//     useEffect(() => {
//         const img = new Image();
//         img.src = cardData.bgUrls[cardData.bgIndex];
//         img.onload = () => {
//             setBgReady(true);
//         };
//     }, [cardData.bgUrls, cardData.bgIndex]);

//     const scaleX = (containerWidth) => containerWidth / baseW;
//     const scaleY = (containerHeight) => containerHeight / baseH;
//     const fs = (baseSize, scale) => baseSize * cardData.fontScale * scale;

//     // For dragging: track which element is dragged
//     const dragState = useRef({
//         draggingKey: null,
//         originMouseX: 0,
//         originMouseY: 0,
//         originPos: { x: 0, y: 0 }
//     });

//     function onMouseDownHandler(fieldKey, e) {
//         e.stopPropagation();
//         dragState.current = {
//             draggingKey: fieldKey,
//             originMouseX: e.clientX,
//             originMouseY: e.clientY,
//             originPos: { ...(cardData[fieldKey]) }
//         };
//         window.addEventListener("mousemove", onMouseMove);
//         window.addEventListener("mouseup", onMouseUp);
//     }

//     function onMouseMove(e) {
//         const ds = dragState.current;
//         if (!ds.draggingKey) return;
//         const dx = e.clientX - ds.originMouseX;
//         const dy = e.clientY - ds.originMouseY;
//         const newPos = { x: ds.originPos.x + dx, y: ds.originPos.y + dy };
//         setCardData((prev) => ({
//             ...prev,
//             [ds.draggingKey]: newPos
//         }));
//     }

//     function onMouseUp(e) {
//         window.removeEventListener("mousemove", onMouseMove);
//         window.removeEventListener("mouseup", onMouseUp);
//         dragState.current.draggingKey = null;
//     }

//     async function captureCard() {
//         if (!cardRef.current) return;
//         // Use html2canvas to convert the card DOM to canvas
//         const canvas = await html2canvas(cardRef.current, {
//             backgroundColor: null,
//             scale: 2 // you can adjust for resolution
//         });
//         const dataUrl = canvas.toDataURL("image/png");
//         // You can then prompt user to download or share
//         const link = document.createElement("a");
//         link.href = dataUrl;
//         link.download = `visiting_card_${Date.now()}.png`;
//         link.click();
//     }

//     return (
//         <div style={{ padding: 16 }}>
//             <div
//                 ref={cardRef}
//                 style={{
//                     width: baseW,
//                     height: baseH,
//                     position: "relative",
//                     backgroundImage: `url(${cardData.bgUrls[cardData.bgIndex]})`,
//                     backgroundSize: "cover",
//                     border: "1px solid #ccc"
//                 }}
//             >
//                 {[
//                     { key: "namePos", text: cardData.businessName, base: 24, bold: true },
//                     { key: "ownerPos", text: cardData.ownerName, base: 18, bold: false },
//                     { key: "phonePos", text: cardData.phoneNumber, base: 16, bold: false },
//                     { key: "emailPos", text: cardData.email, base: 16, bold: false },
//                     { key: "addressPos", text: cardData.address, base: 14, bold: false }
//                 ].map(({ key, text, base, bold }) => {
//                     const pos = cardData[key];
//                     return (
//                         <div
//                             key={key}
//                             onMouseDown={(e) => onMouseDownHandler(key, e)}
//                             style={{
//                                 position: "absolute",
//                                 left: pos.x,
//                                 top: pos.y,
//                                 cursor: "move",
//                                 whiteSpace: "pre-line",
//                                 fontFamily: cardData.fontFamily,
//                                 fontSize: fs(base, (scaleX(1) + scaleY(1)) / 2),
//                                 fontWeight: bold ? "bold" : "normal",
//                                 color: cardData.textColor,
//                                 textShadow: "0px 0px 2px rgba(0,0,0,0.6)"
//                             }}
//                         >
//                             {text}
//                         </div>
//                     );
//                 })}
//             </div>

//             <div style={{ marginTop: 16 }}>
//                 <button onClick={captureCard}>Preview & Download</button>
//             </div>

//             <div style={{ marginTop: 24 }}>
//                 {/* Simple form fields */}
//                 <div>
//                     <label>Business Name:</label>
//                     <input
//                         type="text"
//                         value={cardData.businessName}
//                         onChange={(e) =>
//                             setCardData((prev) => ({
//                                 ...prev,
//                                 businessName: e.target.value
//                             }))
//                         }
//                     />
//                 </div>
//                 <div>
//                     <label>Owner Name:</label>
//                     <input
//                         type="text"
//                         value={cardData.ownerName}
//                         onChange={(e) =>
//                             setCardData((prev) => ({
//                                 ...prev,
//                                 ownerName: e.target.value
//                             }))
//                         }
//                     />
//                 </div>
//                 <div>
//                     <label>Phone:</label>
//                     <input
//                         type="text"
//                         value={cardData.phoneNumber}
//                         onChange={(e) =>
//                             setCardData((prev) => ({
//                                 ...prev,
//                                 phoneNumber: e.target.value
//                             }))
//                         }
//                     />
//                 </div>
//                 <div>
//                     <label>Email:</label>
//                     <input
//                         type="text"
//                         value={cardData.email}
//                         onChange={(e) =>
//                             setCardData((prev) => ({
//                                 ...prev,
//                                 email: e.target.value
//                             }))
//                         }
//                     />
//                 </div>
//                 <div>
//                     <label>Address:</label>
//                     <textarea
//                         value={cardData.address}
//                         onChange={(e) =>
//                             setCardData((prev) => ({
//                                 ...prev,
//                                 address: e.target.value
//                             }))
//                         }
//                     />
//                 </div>
//             </div>
//         </div>
//     );
// }
// export default VisitingCardEditor;